import { CustomAxiosType } from "../types/types";
import { client } from "./client";

/** 빅데이터 분석 조회 조건. 범위(소속)는 서버가 로그인 계정 기준으로 다시 정한다 */
export interface AnalysisFilter {
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd (포함)
  groupId?: string; // "all" 또는 하위 소속 아이디
  vhclTypeCd?: number | null;
}

type Num = number | string | null | undefined;

export interface AnalysisKpi {
  trips: Num;
  vehicles: Num;
  hours: Num;
  km: Num;
  open_trips: Num;
  odd_trips: Num;
  rollover: Num;
  danger: Num;
  idle: Num;
  leave_area: Num;
  theft: Num;
  enter_danger: Num;
  rollover_false: Num;
  rollover_119: Num;
  rollover_center: Num;
  rollover_pending: Num;
  danger_to_rollover: Num;
  rollover_handle_min: Num;
}

export interface AnalysisOverview {
  period: {
    start: string;
    end: string;
    prevStart: string;
    prevEnd: string;
    scope: string | null;
  };
  kpi: AnalysisKpi;
  prevKpi: AnalysisKpi;
  devices: { registered: Num; active7d: Num; active30d: Num };
  monthly: { ym: string; trips: Num; vehicles: Num; hours: Num; km: Num }[];
  monthlyEvents: { ym: string; rollover: Num; danger: Num; area: Num; etc: Num }[];
  lastYearMonthly: { ym: string; trips: Num; vehicles: Num; hours: Num; km: Num }[];
  lastYearMonthlyEvents: { ym: string; rollover: Num; danger: Num; area: Num; etc: Num }[];
  weekHour: { dw: number; hr: number; n: Num }[];
  weekHourEvents: { dw: number; hr: number; n: Num; rollover?: Num; danger?: Num }[];
  duration: { bucket: string; n: Num }[];
  byType: {
    type_cd: number | string;
    name: string;
    registered?: Num;
    trips?: Num;
    vehicles?: Num;
    hours?: Num;
    km?: Num;
    rollover?: Num;
    danger?: Num;
    rollover_false?: Num;
    rollover_119?: Num;
    danger_to_rollover?: Num;
  }[];
  vhclTypes: { code: number | string; name: string }[];
}

export interface AnalysisEventPoint {
  id: number;
  type: string; // 2 전복, 3 위험운전, 5 시동방치, 6 사용지역이탈, 7 도난감지, 9 위험지역진입
  dt: string;
  lat: number;
  lot: number;
  speed: Num;
  status: number;
  result: "pending" | "false" | "119" | "center" | "to_rollover" | "done";
  type_cd: number | null;
  vhcl_nm?: string | null;
  dvc_sn?: string | null;
  ogdp_nm?: string | null;
}

/** 처리 결과 분류 (차량전복 처리 결과 항목) */
export type EventListCategory = "119" | "center" | "false" | "other" | "pending" | "to_rollover";

/** 처리 결과별 이벤트 목록 한 줄 */
export interface AnalysisEventListRow {
  id: number;
  type: string;
  dt: string;
  dvc_sn: string | null;
  vhcl_nm: string | null;
  type_cd: number | null;
  ogdp_nm: string | null;
  speed: Num;
  status: number | null;
  rmv_rsn: string | null;
  rmv_dt: string | null;
  handle_min: Num;
  addr: string | null;
}

/** 원시 위치로그 한 줄 (tb_pstn_log) */
export interface AnalysisRawRow {
  t: string; // 로그 수집 일시
  reg: string | null; // 로그 등록 일시
  stts: number | null; // 0 위치, 1 시동ON, 2 시동OFF
  evt: number | null; // 0 정상, 1 시동ON, 2 시동OFF, 3 헬스체크, 11 전복사고
  lat: Num;
  lot: Num;
  speed: Num;
  heading: Num;
  fix: Num;
  sat: Num;
  hdop: Num;
  tiltx: Num;
  tilty: Num;
  tiltz: Num;
  gyrox: Num;
  gyroy: Num;
  gyroz: Num;
  vib: Num;
  vvolt: Num;
  bvolt: Num;
  cpu: Num;
  mvmn: Num;
  acml: Num;
  rsrp: Num;
}

export interface AnalysisEventRaw {
  event: {
    id: number;
    type: string;
    dt: string;
    dvc_sn: string | null;
    vhcl_id: number | null;
    vhcl_nm: string | null;
    type_cd: number | null;
    type_nm: string | null;
    ogdp_nm: string | null;
    lat: Num;
    lot: Num;
    speed: Num;
    status: number | null;
    rmv_rsn: string | null;
    rmv_dt: string | null;
    addr: string | null;
    opr_id: number | null;
  };
  beforeMin: number;
  afterMin: number;
  from: string | null;
  to: string | null;
  rows: AnalysisRawRow[];
}

export interface AnalysisRegion {
  adm_cd: string;
  adm_nm: string;
  sd_nm: string;
  sgg_nm: string;
  trips: number;
  hours: number;
  rollover: number;
  danger: number;
  area: number;
  etc: number;
}

export interface AnalysisRegions {
  regions: AnalysisRegion[];
  polygons: Record<string, [number, number][]>;
  unmatchedTrips: number;
  unmatchedEvents: number;
}

const body = (f: AnalysisFilter) => ({
  startDate: f.startDate,
  endDate: f.endDate,
  groupId: f.groupId ?? "all",
  vhclTypeCd: f.vhclTypeCd ?? null,
});

// 요약 KPI · 월별 · 요일×시간대 · 운행시간 분포 · 차종별
export const getAnalysisOverview = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisOverview>>(
    `/api/analysis/overview/get`,
    body(f)
  );
  return response.data.result;
};

// 지도용 이벤트 지점 (최대 2만 건)
export const getAnalysisEvents = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisEventPoint[]>>(
    `/api/analysis/events/get`,
    body(f)
  );
  return response.data.result ?? [];
};

// 처리 결과별 이벤트 목록 (최대 2000건)
export const getAnalysisEventList = async (f: AnalysisFilter, category: EventListCategory) => {
  const response = await client.post<CustomAxiosType<AnalysisEventListRow[] | null>>(
    `/api/analysis/events/list/get`,
    { ...body(f), category }
  );
  return response.data.result ?? [];
};

// 이벤트 한 건의 차량 정보와 앞뒤 원시 위치로그
export const getAnalysisEventRaw = async (id: number, beforeMin: number, afterMin: number) => {
  const response = await client.post<CustomAxiosType<AnalysisEventRaw>>(
    `/api/analysis/event/raw/get`,
    { id, beforeMin, afterMin }
  );
  return response.data.result;
};

// 행정동별 집계 + 단순화한 경계
export const getAnalysisRegions = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisRegions>>(
    `/api/analysis/regions/get`,
    body(f)
  );
  return response.data.result;
};
